/**
 * Интерцептор для обработки токенов авторизации
 * Позволяет автоматически обновлять токены и повторять запросы при ошибках аутентификации
 */

import { checkServerIP } from './server-check';

// Импортируем URL API из переменной окружения
const API_URL = (process.env.VUE_APP_API_URL || '').replace(/\/+$/, '');
const DEBUG_AUTH = process.env.VUE_APP_DEBUG_AUTH === 'true';

const authDebug = (...args) => {
  if (DEBUG_AUTH) {
    console.debug('[auth]', ...args);
  }
};

// Создаем очередь запросов, ожидающих обновление токена
let isRefreshing = false;
let failedQueue = [];

/**
 * Обрабатывает запросы в очереди после обновления токена
 * @param {string} token - Новый токен доступа
 * @param {boolean} success - Признак успешности обновления токена
 */
const processQueue = (token, success) => {
  failedQueue.forEach(prom => {
    if (success) {
      prom.resolve(token);
    } else {
      prom.reject();
    }
  });
  
  failedQueue = [];
};

/**
 * Выполняет запрос к API с поддержкой автоматического обновления токена
 * @param {string} path - Путь запроса API
 * @param {object} options - Опции запроса
 * @returns {Promise} - Результат запроса
 */
export async function authFetch(path, options = {}) {
  // Проверяем настройку API URL
  if (!API_URL) {
    throw new Error('API base URL is not configured');
  }

  // Конструируем URL
  const url = new URL(path, API_URL.endsWith('/') ? API_URL : `${API_URL}/`);
  authDebug('request', url.toString(), options.method || 'GET');
  
  // Добавляем параметры запроса
  if (options.params) {
    Object.entries(options.params).forEach(([key, value]) => {
      if (value != null) url.searchParams.append(key, value);
    });
  }

  // Настраиваем заголовки
  const headers = {
    'Accept': 'application/json',
    ...(options.headers || {})
  };

  // Добавляем заголовок Content-Type, если его нет и есть тело запроса
  if (options.body && !headers['Content-Type'] && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  // Если не установлен заголовок Authorization и есть токен, добавляем его
  if (!headers['Authorization']) {
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // Формируем опции запроса
  const fetchOptions = {
    method: options.method || 'GET',
    headers,
    body: options.body instanceof FormData || typeof options.body === 'string' 
        ? options.body 
        : options.body ? JSON.stringify(options.body) : undefined,
    credentials: options.credentials || 'include'
  };

  try {
    // Проверяем IP сервера перед выполнением запроса
    checkServerIP(url.toString());
    
    // Выполняем запрос
    const response = await fetch(url.toString(), fetchOptions);
    authDebug('response', url.toString(), response.status);
    
    // Если запрос успешен, возвращаем результат
    if (response.ok) {
      return response.status === 204 ? null : await response.json();
    }
    
    // Если ошибка 401 (неавторизован), пробуем обновить токен
    if (response.status === 401) {
      authDebug('401 received', url.toString());
      // Получаем refresh token
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        authDebug('no refresh token, redirect to /login');
        // Если нет refresh токена, выходим из системы
        window.location.href = '/login';
        throw new Error('Authentication required');
      }
      
      // Если процесс обновления токена уже идет, добавляем запрос в очередь
      if (isRefreshing) {
        authDebug('refresh in progress, queue request', url.toString());
        try {
          // Ждем, пока текущий процесс обновления токена завершится
          const newToken = await new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
          
          // Повторяем запрос с новым токеном
          headers['Authorization'] = `Bearer ${newToken}`;
          const newResponse = await fetch(url.toString(), {
            ...fetchOptions,
            headers
          });
          
          authDebug('retry after refresh (queued)', url.toString(), newResponse.status);
          return newResponse.status === 204 ? null : await newResponse.json();
        } catch (err) {
          authDebug('refresh failed for queued request', url.toString());
          throw new Error('Failed to refresh token');
        }
      }
      
      // Начинаем процесс обновления токена
      isRefreshing = true;
      authDebug('start refresh');
      
      try {
        // Запрос на обновление токена
        const refreshUrl = new URL('/token/refresh', API_URL.endsWith('/') ? API_URL : `${API_URL}/`);
        authDebug('refresh request', refreshUrl.toString());
        const refreshResponse = await fetch(refreshUrl.toString(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ refresh_token: refreshToken })
        });
        
        if (!refreshResponse.ok) {
          authDebug('refresh failed', refreshResponse.status);
          // Если не удалось обновить токен, выходим из системы
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          processQueue(null, false);
          window.location.href = '/login';
          throw new Error('Failed to refresh token');
        }
        
        // Получаем новые токены
        const tokens = await refreshResponse.json();
        authDebug('refresh ok, tokens updated');
        
        // Сохраняем новые токены
        localStorage.setItem('token', tokens.access_token);
        localStorage.setItem('refresh_token', tokens.refresh_token);
        
        // Обрабатываем очередь запросов
        processQueue(tokens.access_token, true);
        
        // Повторяем исходный запрос с новым токеном
        headers['Authorization'] = `Bearer ${tokens.access_token}`;
        const newResponse = await fetch(url.toString(), {
          ...fetchOptions,
          headers
        });
        
        // Завершаем процесс обновления токена
        isRefreshing = false;
        authDebug('retry after refresh', url.toString(), newResponse.status);
        
        if (!newResponse.ok) {
          const errorData = await parseError(newResponse);
          throw new Error(formatErrorMessage(errorData) || `HTTP ${newResponse.status}`);
        }
        
        return newResponse.status === 204 ? null : await newResponse.json();
      } catch (error) {
        // Если возникла ошибка при обновлении токена
        isRefreshing = false;
        processQueue(null, false);
        authDebug('refresh error', error.message || error);
        throw error;
      }
    } else {
      // Для других ошибок
      const errorData = await parseError(response);
      throw new Error(formatErrorMessage(errorData) || `HTTP ${response.status}`);
    }
  } catch (error) {
    console.error('API request failed:', error);
    throw new Error(error.message || 'Network request failed');
  }
}

/**
 * Преобразует ответ об ошибке в объект
 */
async function parseError(response) {
  try {
    return await response.json();
  } catch {
    return { message: response.statusText };
  }
}

/**
 * Форматирует сообщение об ошибке из ответа API
 */
function formatErrorMessage(errorData) {
  if (!errorData) return null;
  
  if (Array.isArray(errorData.detail)) {
    return errorData.detail.map(err => err.msg).join(', ');
  } else if (typeof errorData.detail === 'string') {
    return errorData.detail;
  } else if (errorData.message) {
    return errorData.message;
  }
  
  return null;
}

/**
 * Получает новые токены доступа по refresh-токену
 * @returns {Promise} - Объект с новыми токенами
 */
export async function refreshToken() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const refreshUrl = new URL('/token/refresh', API_URL.endsWith('/') ? API_URL : `${API_URL}/`);
  const response = await fetch(refreshUrl.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ refresh_token: refreshToken })
  });

  if (!response.ok) {
    throw new Error('Failed to refresh token');
  }

  const tokens = await response.json();
  localStorage.setItem('token', tokens.access_token);
  localStorage.setItem('refresh_token', tokens.refresh_token);
  
  return tokens;
}

export default {
  authFetch,
  refreshToken
}; 
